import { computed, h, onScopeDispose, ref } from 'vue'

/**
 * How many rows one read asks for. Named for the same two reasons `useAdministrationCollection`
 * names its own: the layout paginates nothing, and the store counts pages only once somebody
 * names a size.
 */
const PAGE_SIZE = 50

/**
 * The backing store for <AdminCollectionLayout> over one of the identity SDK's two
 * administration stores — Humans or Machines.
 *
 * `useAdministrationCollection`'s role, played against the identity server. That composable
 * sends the typed FQL to a server that converts it and reads the searchable surface from the
 * same server; the identity server does neither, so the surface comes from the descriptor
 * (`identityCollections.js`) and the descriptor turns the query into the list's parameters.
 * Everything else is the same shape: a debounced query, a one-based page, the read failure the
 * banner shows, and the `config` the layout renders.
 *
 * There is no sort: the identity server lists by name and takes no order. The layout's column
 * sort is ignored rather than applied to one page of a paged list.
 *
 * @param {Object} config
 * @param {Object} config.store      `humansStore()` or `machinesStore()`.
 * @param {Object} config.descriptor `HUMANS` or `MACHINES` from identityCollections.js.
 * @param {string} config.title      What the screen calls this collection.
 * @param {string} config.storageKey Where the layout persists its view state.
 */
export function useIdentityCollection({ store, descriptor, title, storageKey, debounceMs = 250 }) {
    const query = ref('')
    const page = ref(1)
    const readFailure = ref(null)
    const writeFailure = ref(null)
    // Whether a page of rows has come back yet. Until it has, an empty list is not an answer.
    const arrived = ref(false)
    let timer = null

    /**
     * Every read this screen makes, and the one place a refused read is held. `store.error`
     * catches writes too, and every write reports beside the control that made it.
     */
    async function read(work) {
        const ok = await work()
        if (ok === false) {
            readFailure.value = store.error
        } else {
            readFailure.value = null
        }
        return ok
    }

    async function runList(nextPage) {
        page.value = nextPage
        const ok = await read(() => store.list({
            filter: descriptor.listFilterOf(query.value),
            page: nextPage,
            pageSize: PAGE_SIZE
        }))
        arrived.value = true
        return ok
    }

    // Back to the first page: page four of one query is not page four of the next.
    function schedule() {
        clearTimeout(timer)
        timer = setTimeout(() => runList(1), debounceMs)
    }

    function onQuery(next) {
        query.value = next ?? ''
        schedule()
    }

    /** What the paginator calls. The pending keystroke is dropped first. */
    function goToPage(nextPage) {
        clearTimeout(timer)
        return runList(nextPage)
    }

    onScopeDispose(function () {
        clearTimeout(timer)
    })

    /**
     * A write, then the page and the held record read again. Stops on a refusal so the
     * refresh does not clear the very error the screen is about to show.
     */
    async function write(work) {
        writeFailure.value = null
        const answer = await work()
        if (answer === false || answer === null) {
            writeFailure.value = store.error
            return false
        }
        await read(() => store.refresh())
        return answer
    }

    /**
     * What a create answered, held for the one dialog that shows it and nothing else. A
     * Machine's create answers its client secret once; the view clears this the moment the
     * person says they have copied it, and nothing can show it again.
     */
    const issued = ref(null)

    async function onCreate(record) {
        const answer = await write(() => descriptor.create(store, record))
        if (answer === false) {
            return false
        }
        issued.value = answer
        return true
    }

    function renderEmpty() {
        const style = { padding: '50px', textAlign: 'center', color: 'var(--p-text-muted-color)', fontSize: '14px' }
        if (!arrived.value) {
            return h('div', { style, role: 'status' }, [h('i', { class: 'pi pi-spin pi-spinner', 'aria-label': 'Loading' })])
        }
        let text = 'No records match this search.'
        if (query.value.trim() === '') {
            text = `No ${title.toLowerCase()} yet.`
        }
        return h('div', { style }, text)
    }

    const rows = computed(() => store.documents.map(descriptor.rowOf))

    const config = computed(() => ({
        storageKey,
        idKey: 'id',
        kindLabel: title,
        primaryLabel: 'Name',
        data: rows.value,
        total: store.totalCount,
        offset: (page.value - 1) * PAGE_SIZE,
        renderEmpty,
        fieldDefinitions: descriptor.fieldDefinitions,
        fieldMeta: descriptor.fieldMeta,
        primary: (record) => record.name || '(unnamed)',
        secondary: descriptor.secondary,
        tableColumns: descriptor.tableColumns,
        searchPlaceholder: descriptor.searchPlaceholder,
        searchHelp: descriptor.searchHelp,
        // Nothing on these routes edits a record's fields, and nothing deletes: a Machine is
        // retired from its own panel, and a Human is closed by the person.
        hideEdit: true,
        hideDelete: true,
        hideNew: descriptor.hideNew,
        newRecord: descriptor.newRecord ?? undefined,
        onCreate
    }))

    return {
        config,
        onQuery,
        readFailure,
        writeFailure,
        issued,
        page,
        pageSize: PAGE_SIZE,
        goToPage,
        write
    }
}
